import { OASElement } from '@oas-ui/core'
import { isRtl } from '../../shared/direction.js'

/** 窗口操作动作（与 oas-titlebar 的 window-actions 同一契约） */
export type TrafficLightAction = 'minimize' | 'maximize' | 'close'

/** macOS 交通灯左右顺序（LTR：关闭 / 最小化 / 最大化） */
const ORDER: readonly TrafficLightAction[] = ['close', 'minimize', 'maximize']

/** 悬停时在各圆点上显示的符号（原创 SVG，currentColor） */
const GLYPHS: Record<TrafficLightAction, string> = {
  close:
    '<svg viewBox="0 0 10 10" width="6" height="6" aria-hidden="true" focusable="false"><path d="M2 2l6 6M8 2l-6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
  minimize:
    '<svg viewBox="0 0 10 10" width="6" height="6" aria-hidden="true" focusable="false"><path d="M2 5h6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
  // 最大化 = 两枚外向实心三角（左上 / 右下，展开语义）。曾误写成共享中心顶点的双折线
  // （M2 2l3 3 3-3 M2 8l3-3 3 3）——四条斜边交于圆心，渲染成与 close 相同的 ×。
  maximize:
    '<svg viewBox="0 0 10 10" width="6" height="6" aria-hidden="true" focusable="false"><path d="M1.6 1.6h3.2L1.6 4.8zM8.4 8.4H5.2L8.4 5.2z" fill="currentColor"/></svg>',
}

const STYLE = `
:host {
  display: inline-flex;
  align-items: center;
  /* 圆点色为 macOS 惯例「固定品牌色」——刻意明暗一致、不随主题翻转，属组件级常量例外
     （同 slider 色轨 / color-picker 色相光谱）；宿主可经这些变量覆写 */
  --oas-traffic-lights-close: #ff5f57;
  --oas-traffic-lights-minimize: #febc2e;
  --oas-traffic-lights-maximize: #28c840;
  --oas-traffic-lights-symbol: rgba(0, 0, 0, 0.55);
  --oas-traffic-lights-size: 12px;
  --oas-traffic-lights-gap: 8px;
  font-family: inherit;
  /* 整件退出窗口拖动（覆盖圆点间距与 host 内边距，避免缝隙仍是 drag 区）；HC 保留交通灯语义色 */
  -webkit-app-region: no-drag;
  forced-color-adjust: none;
}
:host([hidden]) {
  display: none !important;
}
.group {
  display: inline-flex;
  align-items: center;
  gap: var(--oas-traffic-lights-gap);
}
/* RTL：顺序镜像（逻辑方向） */
:host([data-rtl]) .group {
  flex-direction: row-reverse;
}
.dot {
  appearance: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--oas-traffic-lights-size);
  height: var(--oas-traffic-lights-size);
  padding: 0;
  border: none;
  border-radius: var(--oas-radius-full, 999px);
  cursor: pointer;
  color: transparent;
  transition: color var(--oas-transition-fast, 120ms) ease;
}
.dot[data-action='close'] {
  background: var(--oas-traffic-lights-close);
}
.dot[data-action='minimize'] {
  background: var(--oas-traffic-lights-minimize);
}
.dot[data-action='maximize'] {
  background: var(--oas-traffic-lights-maximize);
}
/* 悬停 / 键盘聚焦时各点显示符号；符号恒为深色（macOS 行为，明暗主题一致，不随主题翻白） */
.group:hover .dot:not(:disabled),
.group:focus-within .dot:not(:disabled) {
  color: var(--oas-traffic-lights-symbol, rgba(0, 0, 0, 0.55));
}
.dot:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
/* 高对比模式：焦点环切系统 Highlight 色（forced-color-adjust:none 下 box-shadow 环不切系统色，补显式 outline） */
@media (forced-colors: active) {
  .dot:focus-visible {
    outline: 2px solid Highlight;
    box-shadow: none;
  }
}
:host([disabled]) .dot {
  cursor: not-allowed;
  opacity: 0.5;
}
.dot svg {
  display: block;
}
`

/**
 * oas-traffic-lights —— macOS 交通灯窗口控件（关闭 / 最小化 / 最大化 三圆点）。
 *
 * 置于 `oas-titlebar` 的 `slot="leading"` 即得 macOS 形态（左侧交通灯 + 居中标题）。
 * 点击派发 `oas-window-action`（detail `{ action }`），与 `oas-titlebar` 的 `window-actions`
 * 同一事件契约——宿主统一处理窗口动作，无需区分来源。
 *
 * 属性：`disabled`（布尔，全禁）；`dir`（方向，RTL 顺序镜像）。
 * 事件：`oas-window-action`（detail `{ action: 'close'|'minimize'|'maximize' }`）。
 * 部件：`group` / `dot`。
 * ARIA：容器 `role="group"` + 可读名；每点 `aria-label`（i18n，随 locale）。
 */
export class OASTrafficLights extends OASElement {
  static override get observedAttributes(): string[] {
    return ['disabled', 'dir']
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板 */
  private template(): string {
    const dots = ORDER.map(
      (a) =>
        `<button class="dot" part="dot" data-action="${a}" type="button" aria-label="${this.t(`titlebar.${a}`)}">${GLYPHS[a]}</button>`,
    ).join('')
    return `<style>${STYLE}</style><div class="group" part="group" role="group" aria-label="${this.t('titlebar.windowControls')}">${dots}</div>`
  }

  private bind(): void {
    for (const btn of this.shadow.querySelectorAll<HTMLButtonElement>('.dot')) {
      btn.addEventListener('click', () => {
        if (this.hasAttr('disabled')) return
        this.emit('window-action', { action: btn.dataset.action })
      })
    }
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（group 部件存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('.group')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    this.toggleAttribute('data-rtl', isRtl(this))
    for (const btn of this.shadow.querySelectorAll<HTMLButtonElement>('.dot')) {
      const action = btn.dataset.action as TrafficLightAction | undefined
      if (action) btn.setAttribute('aria-label', this.t(`titlebar.${action}`))
      btn.disabled = this.hasAttr('disabled')
    }
    const group = this.shadow.querySelector<HTMLElement>('[part="group"]')
    group?.setAttribute('aria-label', this.t('titlebar.windowControls'))
  }
}
